/**
 * Браузерное окружение сессии — швы для тестов (`@srm/core/testing`) и для сред без части API. Приложение их не
 * передаёт: умолчания берут глобальные объекты в момент вызова, а не при импорте или создании фабрики, поэтому
 * `vi.stubGlobal`, фейковые таймеры и jsdom работают и после создания экземпляра.
 */

/** Канал между вкладками — то подмножество BroadcastChannel, которым пользуется ядро. */
export interface AuthChannel {
    postMessage(message: unknown): void;
    addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
    removeEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
    close(): void;
}

/** Web Locks (`navigator.locks`): ротация refresh — одна на все вкладки. */
export type AuthLocks = Pick<LockManager, 'request'>;

export interface AuthVisibility {
    isVisible(): boolean;
    subscribe(listener: () => void): () => void;
}

/** Адрес страницы: повторный вход через IdP уводит с неё полностью, чтобы оборвать запросы на старых токенах. */
export interface AuthLocation {
    /** pathname + search + hash текущей страницы (с basename приложения). */
    currentPath(): string;
    assign(url: string): void;
}

export interface AuthEnvironment {
    /** localStorage; null — недоступно (приватный режим, запрет cookie). */
    readonly storage: Storage | null;
    /** sessionStorage вкладки; null — недоступно. */
    readonly sessionStorage: Storage | null;
    /** null — без Web Locks: single-flight только внутри вкладки. */
    readonly locks: AuthLocks | null;
    /** null — без BroadcastChannel: события идут запасным путём через storage. */
    createChannel(name: string): AuthChannel | null;
    /** `storage`-событие localStorage из других вкладок (в своей вкладке браузер его не шлёт). */
    onStorage(listener: (key: string | null, newValue: string | null) => void): () => void;
    /** `document.cookie`. */
    cookies(): string;
    now(): number;
    /** Разовый таймер; возвращает отмену. */
    setTimer(callback: () => void, ms: number): () => void;
    visibility: AuthVisibility;
    /** Ввод пользователя (указатель, клавиатура, колесо, касание) — для пульса присутствия. */
    activity: { subscribe(listener: () => void): () => void };
    fetch: typeof fetch;
    /** Адрес страницы — для повторного входа через IdP. */
    location: AuthLocation;
}

const INPUT_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

const webStorage = (kind: 'localStorage' | 'sessionStorage'): Storage | null => {
    try {
        return globalThis[kind] ?? null;
    } catch {
        // SecurityError при обращении к хранилищу (запрет cookie для сайта).
        return null;
    }
};

const browserLocks = (): AuthLocks | null =>
    typeof navigator !== 'undefined' && typeof navigator.locks?.request === 'function' ? navigator.locks : null;

const browserChannel = (name: string): AuthChannel | null => {
    if (typeof BroadcastChannel === 'undefined') return null;
    try {
        return new BroadcastChannel(name);
    } catch {
        return null;
    }
};

const browserVisibility: AuthVisibility = {
    isVisible: () => typeof document === 'undefined' || document.visibilityState === 'visible',
    subscribe(listener) {
        if (typeof document === 'undefined') return () => undefined;
        document.addEventListener('visibilitychange', listener);
        return () => document.removeEventListener('visibilitychange', listener);
    },
};

const browserActivity: AuthEnvironment['activity'] = {
    subscribe(listener) {
        if (typeof window === 'undefined') return () => undefined;
        INPUT_EVENTS.forEach((type) => window.addEventListener(type, listener, { capture: true, passive: true }));
        return () => INPUT_EVENTS.forEach((type) => window.removeEventListener(type, listener, { capture: true }));
    },
};

const browserLocation: AuthLocation = {
    currentPath: () =>
        typeof window === 'undefined' ? '' : window.location.pathname + window.location.search + window.location.hash,
    assign(url) {
        if (typeof window !== 'undefined') window.location.assign(url);
    },
};

/**
 * Окружение с подменами: заданное поле берётся как есть (в том числе `null` — «API нет»), остальные — из браузера
 * в момент обращения.
 */
export function authEnvironment(overrides: Partial<AuthEnvironment> = {}): AuthEnvironment {
    return {
        get storage() {
            return overrides.storage !== undefined ? overrides.storage : webStorage('localStorage');
        },
        get sessionStorage() {
            return overrides.sessionStorage !== undefined ? overrides.sessionStorage : webStorage('sessionStorage');
        },
        get locks() {
            return overrides.locks !== undefined ? overrides.locks : browserLocks();
        },
        createChannel: (name) => (overrides.createChannel ?? browserChannel)(name),
        onStorage(listener) {
            if (overrides.onStorage) return overrides.onStorage(listener);
            if (typeof window === 'undefined') return () => undefined;
            const handler = (e: StorageEvent) => listener(e.key, e.newValue);
            window.addEventListener('storage', handler);
            return () => window.removeEventListener('storage', handler);
        },
        cookies: () =>
            overrides.cookies ? overrides.cookies() : typeof document === 'undefined' ? '' : document.cookie,
        now: () => (overrides.now ? overrides.now() : Date.now()),
        setTimer(callback, ms) {
            if (overrides.setTimer) return overrides.setTimer(callback, ms);
            const id = setTimeout(callback, ms);
            return () => clearTimeout(id);
        },
        get visibility() {
            return overrides.visibility ?? browserVisibility;
        },
        get activity() {
            return overrides.activity ?? browserActivity;
        },
        fetch: (input, init) => (overrides.fetch ?? globalThis.fetch)(input, init),
        get location() {
            return overrides.location ?? browserLocation;
        },
    };
}

/** Чтение хранилища без исключений: Safari Private и запрет cookie бросают и на getItem/setItem. */
export const safeGet = (storage: Storage | null, key: string): string | null => {
    try {
        return storage?.getItem(key) ?? null;
    } catch {
        return null;
    }
};

export const safeSet = (storage: Storage | null, key: string, value: string): void => {
    try {
        storage?.setItem(key, value);
    } catch {
        // квота или запрет хранилища — состояние остаётся в памяти экземпляра
    }
};

export const safeRemove = (storage: Storage | null, key: string): void => {
    try {
        storage?.removeItem(key);
    } catch {
        // запрет хранилища
    }
};
